'use es6';

import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { sortBy } from 'underscore';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentRoutes from 'ContentUtils/Routes';
import { Select } from 'foundations-components/transitional/components/Select';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getCurrentTranslations, getHasCurrentTranslations, getSelectedContentId } from 'ContentEditorUI/redux/selectors/translationSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getLanguageNameFromKey, isMasterTranslation } from 'ContentEditorUI/utils/multiLangUtils';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getIsABTestActivelyRunning } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { getTranslatedFromId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getActiveDomainName } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getSiteSettingsBodyOverrideByDomain } from 'ContentEditorUI/redux/selectors/siteSettingsSelectors';
import { Flex } from 'foundations-components/components/Flex';
import { LanguageIcon } from 'foundations-assets/icons/components/LanguageIcon';
import { TestIcon } from 'foundations-assets/icons/components/TestIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getRenderLanguageForActiveDomain(bodyOverrideByDomain, activeDomainName) {
  if (!bodyOverrideByDomain) {
    return null;
  }
  return bodyOverrideByDomain[activeDomainName] && bodyOverrideByDomain[activeDomainName]['contentLanguage'] || bodyOverrideByDomain[activeDomainName] && bodyOverrideByDomain[activeDomainName]['content_language'];
}
function getIsPrimaryLanguageVariant(language, translation, renderLanguageForActiveDomain) {
  return renderLanguageForActiveDomain ? renderLanguageForActiveDomain === language : isMasterTranslation(translation);
}

/**
 * @param {Object} props
 * @param {string} [props.className] (optional)
 */
const TranslationSelectorContainer = () => {
  const contentId = useSelector(getSelectedContentId);
  const categoryId = useSelector(getCategoryId);
  const hasCurrentTranslations = useSelector(getHasCurrentTranslations);
  const translations = useSelector(getCurrentTranslations);
  const translatedFromId = useSelector(getTranslatedFromId);
  const activeDomainName = useSelector(getActiveDomainName);
  const bodyOverrideByDomain = useSelector(getSiteSettingsBodyOverrideByDomain);
  const isAbTestActivelyRunning = useSelector(getIsABTestActivelyRunning);
  const renderLanguageForActiveDomain = getRenderLanguageForActiveDomain(bodyOverrideByDomain, activeDomainName);
  if (!hasCurrentTranslations) {
    return null;
  }
  const onChangeTranslation = e => {
    if (contentId !== e.target.value) {
      UsageTracker.trackAsBeacon('editorInteraction', {
        action: 'Multilanguage Action',
        event: 'switched-translation',
        isLanguageVariation: !!translatedFromId
      });
      location.href = ContentRoutes.edit(e.target.value, categoryId);
    }
  };
  const TestIconWithTooltip = () => /*#__PURE__*/_jsx(Tooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "translationSelector.iconTooltip"
    }),
    placement: "right",
    children: /*#__PURE__*/_jsx(Flex, {
      display: "inline-flex",
      alignItems: "center",
      paddingInlineStart: "100",
      children: /*#__PURE__*/_jsx(TestIcon, {
        color: "do-not-use-eerie"
      })
    })
  });
  function getTranslationOption(translation) {
    const {
      id,
      language,
      ab,
      mab
    } = translation;
    const languageName = getLanguageNameFromKey(language);
    const isPrimaryLanguageVariant = getIsPrimaryLanguageVariant(language, translation, renderLanguageForActiveDomain);
    return {
      text: isPrimaryLanguageVariant ? I18n.text('translationSelector.masterLanguage', {
        language: languageName
      }) : languageName,
      value: id,
      // Don't show this test icon for pages that have had an A/B test in the past, but have no active variations:
      badge: (ab && isAbTestActivelyRunning || mab) && /*#__PURE__*/_jsx(TestIconWithTooltip, {})
    };
  }
  const getTranslationOptions = () => sortBy(translations.map(getTranslationOption), 'text');
  function makeButtonTextRenderer() {
    const translation = translations.find(({
      id
    }) => id === contentId);
    const {
      language
    } = translation;
    return () => /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(LanguageIcon, {})
      }), getLanguageNameFromKey(language)]
    });
  }
  const translationSelector = /*#__PURE__*/_jsx(Select, {
    "data-onboarding": "translation-variation-selector",
    defaultValue: contentId,
    menuWidth: 300,
    onChange: onChangeTranslation,
    options: getTranslationOptions(),
    buttonUse: 'form',
    ButtonContent: makeButtonTextRenderer(),
    padding: "200"
  });
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "translationSelector.tooltip"
      }),
      placement: "bottom",
      children: translationSelector
    })
  });
};
export default TranslationSelectorContainer;